<template lang="pug">
span.copy-text-button
  v-tooltip(bottom open-delay="200")
    template(#activator="{ on, attrs }")
      v-hover(v-slot="{ hover }" @input="$emit('hover', $event)")
        v-btn(
          v-bind="attrs"
          v-on="on"
          icon
          small
          :color="color"
          :style="{ opacity: opacity === null ? (hover ? 0.92 : 1) : opacity }"
          :loading="copying"
          :aria-label="label"
          @click.stop="copy"
          @keydown.stop
        ).white.copy-hover-transition
          v-icon(size="18") {{ copied ? 'mdi-check' : 'mdi-content-copy' }}
    span {{ label }}
  v-snackbar(v-model="failed" color="error" :timeout="4000") {{ $t('clipboard.failed') }}
</template>

<script>
export default {
  name: 'CopyTextButton',
  props: {
    color: { type: String, default: 'primary' },
    opacity: { type: Number, default: null },
    text: { type: [String, Number], default: '' },
    getText: { type: Function, default: null }
  },
  data () {
    return {
      copied: false,
      copying: false,
      failed: false,
      resetTimer: null
    }
  },
  computed: {
    label () {
      return this.$t(this.copied ? 'clipboard.copied' : 'clipboard.copy')
    }
  },
  beforeDestroy () {
    clearTimeout(this.resetTimer)
  },
  methods: {
    async copy () {
      if (this.copying) return
      const value = String(this.getText ? this.getText() : this.text)
      if (!value.trim()) return
      this.copying = true
      this.copied = false
      this.failed = false
      clearTimeout(this.resetTimer)
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value)
        } else {
          this.copyWithSelection(value)
        }
        this.copied = true
        this.$emit('copied', value)
        this.resetTimer = setTimeout(() => { this.copied = false }, 2000)
      } catch (error) {
        this.failed = true
        this.$emit('error', error)
      } finally {
        this.copying = false
      }
    },
    copyWithSelection (value) {
      const activeElement = document.activeElement
      const selection = window.getSelection()
      const ranges = []
      for (let index = 0; selection && index < selection.rangeCount; index++) {
        ranges.push(selection.getRangeAt(index).cloneRange())
      }
      const field = document.createElement('textarea')
      field.value = value
      field.readOnly = true
      field.style.cssText = 'position:fixed;left:-9999px;top:0'
      document.body.appendChild(field)
      try {
        field.select()
        if (!document.execCommand('copy')) throw new Error('Copy failed')
      } finally {
        document.body.removeChild(field)
        if (activeElement && activeElement.focus) activeElement.focus({ preventScroll: true })
        if (selection) {
          selection.removeAllRanges()
          ranges.forEach(range => selection.addRange(range))
        }
      }
    }
  }
}
</script>

<style lang="sass">
.v-application .copy-hover-transition
  transition-property: color, opacity
  transition-duration: .15s
  transition-timing-function: cubic-bezier(.4, 0, .2, 1)

// Animate shared hover styles once, without additional transitions on descendants.
.v-application .copy-hover-transition *
  transition: none
</style>
